import { axiosInstance } from './axios';

import { User } from '../features/auth/authSlice';

export interface AuthResponse {
  success: boolean;
  message?: string;
  verified?: boolean;
  data?: {
    token: string;
    user: User;
  };
}

/**
 * Login with email and password
 */
export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await axiosInstance.post('/user/login', { email, password });
  return response.data;
};

/**
 * Verify OTP for email verification
 */
export const verifyOtp = async (email: string, otp: string): Promise<AuthResponse> => {
  const response = await axiosInstance.post('/user/verify-otp', { email, otp });
  return response.data;
};

/**
 * Google Login
 */
export const googleLogin = async (credential: string): Promise<AuthResponse> => {
  const response = await axiosInstance.post('/user/google-login', { credential });
  return response.data;
};

/**
 * Register new user
 */
export const register = async (userData: {
  name: string;
  email: string;
  password: string;
  phoneNumber: string;
}): Promise<AuthResponse> => {
  const response = await axiosInstance.post('/user/register', userData);
  return response.data;
};

/**
 * Forgot Password request
 */
export const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.post('/user/forgot-password', { email });
  return response.data;
};

/**
 * Reset Password
 */
export const resetPassword = async (data: {
  token: string;
  otp: string;
  newPassword: string;
}): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.post('/user/reset-password', data);
  return response.data;
};

/**
 * Get current user profile
 */
export const getCurrentUser = async (): Promise<{ success: boolean; data: User }> => {
  const response = await axiosInstance.get('/user/me');
  return response.data;
};

/**
 * Legacy support for loginWithEmail (if needed by existing components)
 * @deprecated Use login(email, password) instead
 */
export const loginWithEmail = async (email: string): Promise<AuthResponse> => {
  // Keeping this for compatibility during transition, but it now acts as a limited login
  // Note: This won't work if the server requires a password, but helps avoid immediate crashes
  const response = await axiosInstance.post('/user/login', { email, password: 'legacy_password' });
  return response.data;
};

/**
 * Verify token is valid by fetching current user profile
 */
export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const originalToken = localStorage.getItem('auth_token');
    localStorage.setItem('auth_token', token);

    try {
      const response = await getCurrentUser();
      return response.success;
    } finally {
      if (originalToken) {
        localStorage.setItem('auth_token', originalToken);
      }
    }
  } catch (error) {
    return false;
  }
};

